import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../lib/adminApi';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import { LogOut, Download, Search, RefreshCw, Users, TrendingUp, CalendarDays, UserX, ExternalLink, SlidersHorizontal } from 'lucide-react';
import StatsCards from './StatsCards';
import SubscribersTable, { ETAPAS, ORIGENES } from './SubscribersTable';
import InsightsPanel from './InsightsPanel';
import OrigenFunnel from './OrigenFunnel';
import TestimonialsManager from './TestimonialsManager';
import ReminderCard from './ReminderCard';
import SettingsCards from './SettingsCards';
import ComparadorStats from './ComparadorStats';
import RulesEditor from './RulesEditor';
import BlogManager from './BlogManager';
import FaqManager from './FaqManager';
import ChatManager from './ChatManager';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';

const PER_PAGE = 25;
const TAB_CLASS = 'rounded-xl px-4 sm:px-5 py-2.5 shrink-0 data-[state=active]:bg-blue-600 data-[state=active]:text-white text-slate-300';

export const AdminDashboard = ({ onLogout }) => {
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [estado, setEstado] = useState('');
  const [etapa, setEtapa] = useState('');
  const [origen, setOrigen] = useState('');
  const [clicks, setClicks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const handleAuthError = useCallback((err) => {
    if (err.response?.status === 401) {
      toast({ title: 'Sesión expirada', description: 'Vuelve a iniciar sesión.', variant: 'destructive' });
      onLogout();
      return true;
    }
    return false;
  }, [onLogout, toast]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: PER_PAGE };
      if (query) params.search = query;
      if (estado) params.estado = estado;
      if (etapa) params.etapa = etapa;
      if (origen) params.origen = origen;
      const [statsRes, subsRes, clicksRes] = await Promise.all([
        adminApi.get(`/newsletter/stats`),
        adminApi.get(`/newsletter/subscribers`, { params }),
        adminApi.get(`/analytics/clicks`),
      ]);
      setStats(statsRes.data.data);
      setSubscribers(subsRes.data.data);
      setTotal(subsRes.data.total);
      setClicks(clicksRes.data);
    } catch (err) {
      if (!handleAuthError(err)) {
        toast({ title: 'Error', description: 'No se pudieron cargar los datos.', variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  }, [page, query, estado, etapa, origen, handleAuthError, toast]);

  useEffect(() => { load(); }, [load]);

  const handleUpdateLead = async (id, payload) => {
    try {
      const { data } = await adminApi.patch(`/newsletter/subscribers/${id}`, payload);
      setSubscribers((prev) => prev.map((s) => (s.id === id ? { ...s, ...data.data } : s)));
      if (payload.etapa) {
        const statsRes = await adminApi.get(`/newsletter/stats`);
        setStats(statsRes.data.data);
      }
      toast({ title: 'Lead actualizado', description: payload.etapa ? `Etapa: ${payload.etapa}` : 'Nota guardada.' });
    } catch (err) {
      if (!handleAuthError(err)) {
        toast({ title: 'Error', description: 'No se pudo actualizar el lead.', variant: 'destructive' });
      }
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (query) params.search = query;
      if (estado) params.estado = estado;
      if (etapa) params.etapa = etapa;
      if (origen) params.origen = origen;
      const res = await adminApi.get(`/newsletter/export`, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads_iwannainsurance_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Exportación lista', description: 'El archivo CSV se descargó correctamente.' });
    } catch (err) {
      if (!handleAuthError(err)) {
        toast({ title: 'Error', description: 'No se pudo exportar el archivo.', variant: 'destructive' });
      }
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-slate-950/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 py-3 sm:py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xl sm:text-2xl font-bold leading-none">
              <span className="text-blue-400">IWANNA</span>{' '}
              <span className="text-red-400 text-base sm:text-lg font-medium">insurance</span>
            </p>
            <p className="text-slate-400 text-xs sm:text-sm mt-1 truncate">Panel de leads del newsletter</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <Link to="/" data-testid="admin-view-site-link" className="hidden sm:inline-flex items-center text-sm text-slate-300 hover:text-white transition-colors">
              <ExternalLink className="h-4 w-4 mr-1" /> Ver sitio
            </Link>
            <Button data-testid="admin-logout-btn" onClick={onLogout} variant="outline" className="h-10 rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white px-3 sm:px-4">
              <LogOut className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Salir</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 sm:py-10">
        <Tabs defaultValue="leads" className="space-y-6 sm:space-y-8">
          <div className="-mx-4 px-4 overflow-x-auto scrollbar-none">
            <TabsList className="bg-white/5 border border-white/10 rounded-2xl p-1 h-auto inline-flex w-max min-w-full sm:min-w-0 flex-nowrap" data-testid="admin-tabs">
              <TabsTrigger value="leads" data-testid="admin-tab-leads" className={TAB_CLASS}>Leads</TabsTrigger>
              <TabsTrigger value="comparador" data-testid="admin-tab-comparador" className={TAB_CLASS}>Comparador</TabsTrigger>
              <TabsTrigger value="contenido" data-testid="admin-tab-contenido" className={TAB_CLASS}>Contenido</TabsTrigger>
              <TabsTrigger value="chat" data-testid="admin-tab-chat" className={TAB_CLASS}>Chat IA</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="chat" className="mt-0" data-testid="admin-panel-chat">
            <ChatManager onAuthError={handleAuthError} />
          </TabsContent>

          <TabsContent value="comparador" className="space-y-8 mt-0" data-testid="admin-panel-comparador">
            <ComparadorStats onAuthError={handleAuthError} />
            <RulesEditor onAuthError={handleAuthError} />
          </TabsContent>

          <TabsContent value="contenido" className="space-y-8 mt-0" data-testid="admin-panel-contenido">
            <BlogManager onAuthError={handleAuthError} />
            <FaqManager onAuthError={handleAuthError} />
            <TestimonialsManager onAuthError={handleAuthError} />
          </TabsContent>

          <TabsContent value="leads" className="space-y-10 mt-0" data-testid="admin-panel-leads">
        <StatsCards stats={stats} items={[
          { key: 'total_subscriptions', label: 'Total de leads', icon: Users, color: 'from-blue-600 to-blue-400' },
          { key: 'last_7_days', label: 'Últimos 7 días', icon: CalendarDays, color: 'from-emerald-600 to-teal-400' },
          { key: 'active_subscriptions', label: 'Activos', icon: TrendingUp, color: 'from-amber-500 to-orange-400' },
          { key: 'cancelled_subscriptions', label: 'Cancelados', icon: UserX, color: 'from-red-600 to-rose-400' },
        ]} />

        <InsightsPanel clicks={clicks} byEtapa={stats?.by_etapa} />

        <OrigenFunnel byOrigen={stats?.by_origen} />

        <ReminderCard onAuthError={handleAuthError} />

        <SettingsCards onAuthError={handleAuthError} />

        <section className="bg-white/5 border border-white/10 rounded-3xl p-4 sm:p-6 space-y-5 sm:space-y-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4 justify-between">
            <form onSubmit={handleSearch} className="flex flex-1 gap-2 sm:gap-3">
              <div className="relative flex-1 lg:max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input
                  data-testid="admin-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar nombre, email, teléfono..."
                  className="h-11 pl-10 bg-slate-900/70 border-white/10 text-white placeholder:text-slate-500 rounded-xl"
                />
              </div>
              <Button type="submit" data-testid="admin-search-btn" className="h-11 rounded-xl bg-blue-600 hover:bg-blue-500 px-4">Buscar</Button>
              <Button type="button" data-testid="admin-toggle-filters-btn" onClick={() => setShowFilters((v) => !v)} variant="outline" className={`lg:hidden h-11 w-11 p-0 rounded-xl border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white ${(estado || etapa || origen) ? 'ring-2 ring-blue-500/60' : ''}`} aria-label="Filtros">
                <SlidersHorizontal className="h-4 w-4" />
              </Button>
            </form>

            <div className={`${showFilters ? 'grid' : 'hidden'} lg:flex grid-cols-2 items-center gap-2 sm:gap-3`} data-testid="admin-filters">
              <select
                data-testid="admin-estado-filter"
                value={estado}
                onChange={(e) => { setEstado(e.target.value); setPage(1); }}
                className="h-11 px-3 sm:px-4 bg-slate-900/70 border border-white/10 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/40 text-sm"
              >
                <option value="">Todos los estados</option>
                <option value="activo">Activos</option>
                <option value="cancelado">Cancelados</option>
              </select>
              <select
                data-testid="admin-etapa-filter"
                value={etapa}
                onChange={(e) => { setEtapa(e.target.value); setPage(1); }}
                className="h-11 px-3 sm:px-4 bg-slate-900/70 border border-white/10 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/40 text-sm"
              >
                <option value="">Todas las etapas</option>
                {ETAPAS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
              </select>
              <select
                data-testid="admin-origen-filter"
                value={origen}
                onChange={(e) => { setOrigen(e.target.value); setPage(1); }}
                className="h-11 px-3 sm:px-4 bg-slate-900/70 border border-white/10 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/40 text-sm"
              >
                <option value="">Todos los orígenes</option>
                {ORIGENES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <Button data-testid="admin-refresh-btn" onClick={load} variant="outline" className="h-11 rounded-xl border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /><span className="ml-2 lg:hidden">Actualizar</span>
              </Button>
              <Button data-testid="admin-export-btn" onClick={handleExport} disabled={exporting || total === 0} className="h-11 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400">
                <Download className="h-4 w-4 mr-2" /> {exporting ? 'Exportando...' : 'Exportar CSV'}
              </Button>
            </div>
          </div>

          <SubscribersTable subscribers={subscribers} loading={loading} onUpdateLead={handleUpdateLead} />

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between text-sm text-slate-400">
            <span data-testid="admin-results-count">{total} resultado{total === 1 ? '' : 's'}</span>
            <div className="flex items-center justify-between sm:justify-end gap-2">
              <Button data-testid="admin-prev-page" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="h-11 sm:h-9 px-4 rounded-lg border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white">Anterior</Button>
              <span data-testid="admin-page-indicator">Página {page} de {totalPages}</span>
              <Button data-testid="admin-next-page" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="h-11 sm:h-9 px-4 rounded-lg border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white">Siguiente</Button>
            </div>
          </div>
        </section>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
